import React, { useState } from "react";
import clsx from "clsx";
import { Link2 } from "lucide-react";
import type { FieldNode } from "../shared/types";
import { COPY } from "../copy";
import { InlineText } from "./InlineText";

/**
 * A field list (design §4.5 main column / §3.2 input and output schemas): one row per field with its
 * anchor (`#param-…`, `#body-…`, `#response-…`; clicking the name copies the link — an error's
 * `field` points here), its JSON type, required / optional, default as a label, the API's words, and
 * its child fields folded under "Show child attributes".
 */

const Pill = ({ children, className }: { children: React.ReactNode; className?: string }) => <span className={clsx("rounded px-1.5 py-0.5 text-headline2", className)}>{children}</span>;

const copyLink = (anchor: string) => {
  const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
  navigator.clipboard?.writeText(url);
  window.history.replaceState(null, "", `#${anchor}`);
};

/** Every anchor below a field — a link to one of them opens the fold. */
const descendantAnchors = (field: FieldNode): string[] => [
  ...(field.children ?? []).flatMap((c) => [c.anchor, ...descendantAnchors(c)]),
  ...(field.variants ?? []).flatMap((v) => v.children.flatMap((c) => [c.anchor, ...descendantAnchors(c)])),
];

const FieldRow = ({ field }: { field: FieldNode }) => {
  // Folded children stay in the page (hidden), so their anchors, search and the prerendered HTML
  // still have them; a link to one of them opens the fold.
  const [open, setOpen] = useState(() => typeof window !== "undefined" && descendantAnchors(field).includes(window.location.hash.slice(1)));
  const hasChildren = Boolean(field.children?.length || field.variants?.length);

  return (
    <li id={field.anchor} className="scroll-mt-24 border-b border-border py-4 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => copyLink(field.anchor)} className="group flex items-center gap-1 font-semibold text-foreground" aria-label={COPY.copyLink}>
          <code className="text-bodySmall">{field.name}</code>
          <Link2 size={12} className="opacity-0 group-hover:opacity-60" />
        </button>
        <span className="text-bodySmall text-muted-foreground">{field.type}</span>
        <Pill className={field.required ? "bg-orange/20 text-foreground" : "bg-muted text-muted-foreground"}>{field.required ? COPY.required : COPY.optional}</Pill>
        {field.nullable ? <Pill className="bg-muted text-muted-foreground">{COPY.nullable}</Pill> : null}
        {field.deprecated ? <Pill className="bg-destructive/20 text-foreground">{COPY.deprecated}</Pill> : null}
        {field.default !== undefined ? (
          <Pill className="bg-muted text-muted-foreground">
            {COPY.defaultLabel}: <code>{field.default}</code>
          </Pill>
        ) : null}
      </div>
      {field.description ? (
        <p className="mt-2 text-bodySmall text-foreground">
          <InlineText text={field.description} />
        </p>
      ) : null}
      {field.constraints.length ? <p className="mt-1 text-bodySmall text-muted-foreground">{field.constraints.join(" · ")}</p> : null}
      {field.values?.length ? (
        <div className="mt-2">
          <p className="text-bodySmall font-semibold text-muted-foreground">{COPY.values}</p>
          <ul className="mt-1 space-y-1">
            {field.values.map((v) => (
              <li key={v.value} className="text-bodySmall">
                <code className="rounded bg-muted px-1 py-0.5">{v.value}</code>
                {v.description ? (
                  <span className="text-muted-foreground">
                    {" — "}
                    <InlineText text={v.description} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {field.example !== undefined ? (
        <p className="mt-1 text-bodySmall text-muted-foreground">
          {COPY.example}: <code>{field.example}</code>
        </p>
      ) : null}
      {hasChildren ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-border px-3 py-1 text-bodySmall text-muted-foreground hover:text-foreground"
            aria-expanded={open}
          >
            {open ? COPY.hideChildren : COPY.showChildren}
          </button>
          {
            <div className="mt-2 rounded-lg border border-border px-4" hidden={!open}>
              {field.children?.length ? <FieldList fields={field.children} /> : null}
              {field.variants?.map((variant) => (
                <div key={variant.title} className="py-3">
                  <p className="text-bodySmall font-semibold text-foreground">{variant.title}</p>
                  {variant.description ? (
                    <p className="text-bodySmall text-muted-foreground">
                      <InlineText text={variant.description} />
                    </p>
                  ) : null}
                  <FieldList fields={variant.children} />
                </div>
              ))}
            </div>
          }
        </div>
      ) : null}
    </li>
  );
};

export const FieldList = ({ fields }: { fields: FieldNode[] }) => (
  <ul className="list-none p-0">
    {fields.map((field) => (
      <FieldRow key={field.anchor} field={field} />
    ))}
  </ul>
);
